/**
 * Integracoes e as consultas de CEP e de CNPJ.
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3.
 */

import { query, type ListParams, type Money } from './comum';

import { api, requestPage } from '../client';

/**
 * O que a plataforma implementa para um provedor.
 *
 * E o codigo que define este valor, e nao a vontade de quem configura. Sem
 * adaptador, nenhuma configuracao faz o provedor ser consultado.
 */
export type IntegrationAdapter = 'nenhum' | 'publico' | 'chaveada' | 'plataforma';

/** Estados de docs/INTEGRATIONS.md, secao 2, sem acrescimo. */
export type IntegrationStatus =
  'nao_configurada' | 'simulada' | 'sandbox' | 'conectada' | 'erro' | 'desativada';

export interface Integration {
  key: string;
  name: string;
  category: string;
  description: string;
  docs_url: string;
  adapter: IntegrationAdapter;
  implemented: boolean;
  /** Adaptador `plataforma`: a situacao vem do servidor, e a empresa nao a escolhe. */
  managed_by_platform: boolean;
  /** A plataforma consulta o provedor agora; `usage_note` diz como. */
  in_use: boolean;
  usage_note: string;
  status: IntegrationStatus;
  notes: string;
  allowed_statuses: IntegrationStatus[];
  last_checked_at: string | null;
  last_error: string;
  changed_label: string;
}

export interface AddressLookup {
  zip_code: string;
  street: string;
  district: string;
  city: string;
  state: string;
}

/**
 * O CNPJ na Receita Federal pela BrasilAPI, no formato dos campos do cadastro
 * (ADR 0092). Campo vazio e campo que a consulta nao preenche; `notes` diz o que ficou
 * de fora e por que.
 */
export interface CompanyLookup {
  document: string;
  source: 'brasilapi';
  source_label: string;
  consulted_at: string;
  legal_name: string;
  trade_name: string;
  registration_situation: '' | 'ativa' | 'suspensa' | 'inapta' | 'baixada' | 'nula';
  registration_situation_text: string;
  registration_situation_date: string | null;
  special_situation: string;
  special_situation_date: string | null;
  head_office: '' | 'matriz' | 'filial';
  legal_nature: string;
  legal_nature_code: string;
  cnae: string;
  cnae_description: string;
  secondary_cnaes: { code: string; description: string }[];
  /** Centavos, sem ponto flutuante. Nulo quando zero ou fora do formato. */
  share_capital: Money | null;
  company_size: '' | 'mei' | 'me' | 'epp';
  company_size_text: string;
  founded_on: string | null;
  email: string;
  phone: string;
  address: {
    zip_code: string;
    street: string;
    number: string;
    complement: string;
    district: string;
    city: string;
    state: string;
    city_code: string;
  };
  simples_option: boolean | null;
  mei_option: boolean | null;
  notes: string[];
}

export const integrations = {
  list: (signal?: AbortSignal) =>
    api.get<Integration[]>('/api/v1/integrations', signal),
  configure: (providerKey: string, status: IntegrationStatus, notes: string) =>
    api.patch<Integration>(`/api/v1/integrations/${providerKey}`, { status, notes }),
  check: (providerKey: string) =>
    api.post<Integration>(`/api/v1/integrations/${providerKey}/check`, {}),
  lookupAddress: (zipCode: string, signal?: AbortSignal) =>
    api.get<AddressLookup>(
      `/api/v1/address-lookup?zip_code=${encodeURIComponent(zipCode)}`,
      signal,
    ),
  /** O CNPJ vai na consulta, e nao no caminho, para nao entrar no log de acesso. */
  lookupCompany: (document: string, signal?: AbortSignal) =>
    api.get<CompanyLookup>(
      `/api/v1/company-lookup?document=${encodeURIComponent(document)}`,
      signal,
    ),
};

// --- Registro de integracoes e consultas a bureau (P-INT) -------------------

/** Familia da chamada. `bureau` e a tela de consultas a bureau. */
export type IntegrationCallKind =
  'bureau' | 'seguradora' | 'cobranca' | 'comunicacao' | 'assinatura' | 'plataforma';

export type IntegrationCallStatus = 'sucesso' | 'parcial' | 'falhou' | 'incerta';

export type IntegrationCallSubjectType =
  | 'tomador'
  | 'corretor'
  | 'segurado'
  | 'operacao'
  | 'apolice'
  | 'endosso'
  | 'integracao';

/** Uma chamada a sistema externo, gravada uma vez, com o resultado. */
export interface IntegrationCall {
  id: string;
  call_type: string;
  call_type_name: string;
  kind: IntegrationCallKind;
  provider_key: string;
  provider_name: string;
  /** Provedor ficticio: nunca e cobrado e nunca vale para decisao. */
  simulated: boolean;
  notice: string;
  status: IntegrationCallStatus;
  subject: { type: IntegrationCallSubjectType; id: string; label: string } | null;
  /** Identificador no fornecedor: o "Id da consulta" do OnPoint. */
  external_reference: string;
  /** Formatado, ou mascarado quando `document_masked`. */
  document: string;
  document_masked: boolean;
  broker_id: string | null;
  query_type_id: string | null;
  credit_consultation_id: string | null;
  attempt: number;
  resent_from_id: string | null;
  message: string;
  error_code: string;
  retryable: boolean;
  /** Nulo e custo desconhecido ou inexistente. */
  cost: Money | null;
  cost_billable: boolean | null;
  started_at: string;
  finished_at: string;
  duration_ms: number;
  requested_label: string;
  can_resend: boolean;
}

export interface IntegrationCallDetail extends IntegrationCall {
  idempotency_key: string;
  resends: IntegrationCall[];
}

export interface IntegrationCallResend {
  resent_from: string;
  /** Nada saiu: um resultado valido foi reaproveitado. */
  reused: boolean;
  message: string;
  call: IntegrationCall | null;
}

export interface IntegrationCallType {
  key: string;
  kind: IntegrationCallKind;
  name: string;
  description: string;
  implemented: boolean;
  resendable: boolean;
  cost_confirmation: boolean;
}

export interface IntegrationBureau {
  key: string;
  name: string;
  category: string;
  adapter: IntegrationAdapter;
  simulated: boolean;
  in_catalog: boolean;
  configured: boolean;
  seen_in_calls: boolean;
}

export interface IntegrationCallFilter extends ListParams {
  kind?: IntegrationCallKind;
  call_type?: string;
  provider_key?: string;
  status?: IntegrationCallStatus;
  /** AAAA-MM-DD, dia no fuso de Brasilia. */
  from?: string;
  /** AAAA-MM-DD, o dia inteiro. */
  to?: string;
  document?: string;
  subject_type?: IntegrationCallSubjectType;
  subject_id?: string;
}

export const integrationCalls = {
  list: (params: IntegrationCallFilter, signal?: AbortSignal) =>
    requestPage<IntegrationCall>(`/api/v1/integration-calls${query(params)}`, signal),
  get: (callId: string, signal?: AbortSignal) =>
    api.get<IntegrationCallDetail>(`/api/v1/integration-calls/${callId}`, signal),
  resend: (callId: string, confirmCost: boolean) =>
    api.post<IntegrationCallResend>(`/api/v1/integration-calls/${callId}/resend`, {
      confirm_cost: confirmCost,
    }),
  types: (signal?: AbortSignal) =>
    api.get<IntegrationCallType[]>('/api/v1/integration-call-types', signal),
  bureaus: (signal?: AbortSignal) =>
    api.get<IntegrationBureau[]>('/api/v1/bureaus', signal),
};

export interface BureauSetting {
  id: string;
  provider_key: string;
  provider_name: string;
  priority: number;
  active: boolean;
  notes: string;
}

/** Tipo de consulta a bureau da empresa, com os bureaus em ordem de prioridade. */
export interface BureauQueryType {
  id: string;
  name: string;
  description: string;
  call_type: string;
  active: boolean;
  sort_order: number;
  bureaus: BureauSetting[];
  changed_label: string;
  created_at: string;
  updated_at: string;
}

export interface BureauQueryTypeInput {
  name?: string;
  description?: string;
  call_type?: string;
  active?: boolean;
  sort_order?: number;
  /** Substitui a lista inteira. */
  bureaus?: {
    provider_key: string;
    priority: number;
    active?: boolean;
    notes?: string;
  }[];
}

export interface BureauQueryHistoryEntry {
  id: string;
  action: string;
  actor_label: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  occurred_at: string;
}

/** Parametrizacao dos bureaus por tipo de consulta. A tela e da parametrizacao. */
export const bureauQueries = {
  list: (signal?: AbortSignal) =>
    api.get<BureauQueryType[]>('/api/v1/settings/bureau-queries', signal),
  create: (input: BureauQueryTypeInput) =>
    api.post<BureauQueryType>('/api/v1/settings/bureau-queries', input),
  update: (queryTypeId: string, input: BureauQueryTypeInput) =>
    api.patch<BureauQueryType>(`/api/v1/settings/bureau-queries/${queryTypeId}`, input),
  remove: (queryTypeId: string) =>
    api.delete<void>(`/api/v1/settings/bureau-queries/${queryTypeId}`),
  bureaus: (queryTypeId: string, signal?: AbortSignal) =>
    api.get<BureauSetting[]>(
      `/api/v1/settings/bureau-queries/${queryTypeId}/bureaus`,
      signal,
    ),
  history: (signal?: AbortSignal) =>
    api.get<BureauQueryHistoryEntry[]>(
      '/api/v1/settings/bureau-queries/history',
      signal,
    ),
};
